import {Box, Container, styled} from '@mui/material';
import {DateTime} from 'luxon';
import React, {useEffect, useMemo} from 'react';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import BackButton from '../shared/BackButton';
import FullScreenLoadingSpinner from '../shared/FullScreenLoading';
import PageSubtitle from '../shared/PageSubtitle';
import PageTitle from '../shared/PageTitle';
import {getTeamSessionGroupDetailAsync} from '../../store/teamSessionGroupSlice';
import TeamSessionsList from '../shared/TeamSessionsList';
import TeamShiftUtilization from '../shared/TeamShiftUtilization';
import PracticeEfficiency from '../shared/PracticeEfficiency/PracticeEfficiency';
import SessionMetaData from '../shared/SessionMetaData';
import TeamSessionPlayerList from './TeamSessionPlayerList';
import {SessionTagCodes} from '../../enums';
import {createSessionTagFilter} from '../../services/sessionTagService';
import {
  PlayerSession,
  TeamSession,
  TeamSessionSessionEvent,
} from '../../interfaces';
import {getMean, getSum, getMax} from '../../services/mathService';
import SubtitleContainer from '../shared/SubtitleContainer';
import ExportSessionModal from '../shared/ExportSessionModal';
import TeamSessionPlayerLoad from './TeamSessionPlayerLoad/TeamSessionPlayerLoad';

interface Props {}

const StyledSessionType = styled('span')(() => ({
  display: 'block',
  textTransform: 'capitalize',
}));

const TeamSessionGroupDetail: React.FC<Props> = () => {
  const {t} = useTranslation();
  const teams = useAppSelector(state => state.team.teams);
  const teamSessionGroupStatus = useAppSelector(
    state => state.teamSessionGroup.status,
  );
  const teamSessionGroup = useAppSelector(
    state => state.teamSessionGroup.teamSessionGroup,
  );
  const teamSessionGroupTeamSessions = useAppSelector(
    state => state.teamSessionGroup.teamSessionGroup?.teamSessions,
  );
  const dispatch = useAppDispatch();
  const {teamId, teamSessionGroupId} = useParams();
  const teamSessionGroupIdInt = useMemo(
    () => parseInt(teamSessionGroupId ?? ''),
    [teamSessionGroupId],
  );
  const teamIdInt = useMemo(() => parseInt(teamId ?? ''), [teamId]);
  const preferredUnits = useAppSelector(
    store => store.profile?.profile?.preferredUnits,
  );

  useEffect(() => {
    dispatch(getTeamSessionGroupDetailAsync(teamSessionGroupIdInt));
  }, [dispatch, teamSessionGroupIdInt]);

  const activeTeam = useMemo(
    () => (teams ?? []).filter(team => team.id === teamIdInt)[0],
    [teams, teamIdInt],
  );

  const title = useMemo(
    () =>
      t('navigation.pageTitle', {
        pageName: t('pages.teamSessionDetail'),
      }),
    [t],
  );

  const sessionType = useMemo(
    () =>
      teamSessionGroup?.teamSessions?.length &&
      teamSessionGroup?.teamSessions[0]?.type,
    [teamSessionGroup?.teamSessions],
  );

  const groupedPlayerSessions = useMemo(
    () =>
      (teamSessionGroupTeamSessions ?? [])
        .filter(
          createSessionTagFilter([
            SessionTagCodes.fullGame,
            SessionTagCodes.period1,
            SessionTagCodes.period2,
            SessionTagCodes.period3,
            SessionTagCodes.overtime,
          ]),
        )
        .flatMap(ts => ts.playerSessions ?? [])
        // group by playerId
        .reduce((acc: PlayerSession[][], curr) => {
          const index = acc.findIndex(psg => psg[0].playerId === curr.playerId);
          if (index === -1) {
            acc.push([curr]);
          } else {
            acc[index].push(curr);
          }
          return acc;
        }, [])
        // create a new playersession object from each group
        .map(playerSessionGroup => ({
          playerSessionIds: playerSessionGroup.map(ps => ps.id),
          player: playerSessionGroup[0].player,
          activeTime:
            playerSessionGroup.filter(ps => ps.hasPlayerData).length > 0
              ? getSum(playerSessionGroup.map(ps => ps.activeTime ?? 0))
              : undefined,
          timeOnIce:
            playerSessionGroup.filter(ps => ps.hasPlayerData).length > 0
              ? getSum(playerSessionGroup.map(ps => ps.timeOnIce ?? 0))
              : undefined,
          strintTotalTime:
            playerSessionGroup.filter(ps => ps.hasPlayerData).length > 0
              ? getSum(playerSessionGroup.map(ps => ps.strintTotalTime ?? 0))
              : undefined,
          energyRatio:
            getMean(
              playerSessionGroup
                .filter(ps => ps.energyRatio)
                .map(ps => ps.energyRatio as number),
            ) ?? undefined,
          strideCountLeft:
            playerSessionGroup.filter(ps => ps.hasPlayerData).length > 0
              ? getSum(playerSessionGroup.map(ps => ps.strideCountLeft ?? 0))
              : undefined,
          strideCountRight:
            playerSessionGroup.filter(ps => ps.hasPlayerData).length > 0
              ? getSum(playerSessionGroup.map(ps => ps.strideCountRight ?? 0))
              : undefined,
          speedAvgV2:
            getMean(
              playerSessionGroup
                .filter(ps => ps.speedAvgV2)
                .map(ps => ps.speedAvgV2),
            ) ?? undefined,
          speedMaxV2:
            getMax(
              playerSessionGroup
                .filter(ps => ps.speedMaxV2)
                .map(ps => ps.speedMaxV2),
            ) ?? undefined,
          accelAvgV2:
            getMean(
              playerSessionGroup
                .filter(ps => ps.accelAvgV2)
                .map(ps => ps.accelAvgV2),
            ) ?? undefined,
          accelMaxV2:
            getMax(
              playerSessionGroup
                .filter(ps => ps.accelMaxV2)
                .map(ps => ps.accelMaxV2),
            ) ?? undefined,
          strintFilteredStrideRateAvgV2:
            getMean(
              playerSessionGroup
                .filter(ps => ps.strintFilteredStrideRateAvgV2)
                .map(ps => ps.strintFilteredStrideRateAvgV2),
            ) ?? undefined,
          strintFilteredStrideRateMaxV2:
            getMax(
              playerSessionGroup
                .filter(ps => ps.strintFilteredStrideRateMaxV2)
                .map(ps => ps.strintFilteredStrideRateMaxV2),
            ) ?? undefined,
          balanceNormalized:
            getMean(
              playerSessionGroup
                .filter(ps => ps.balanceNormalized)
                .map(ps => ps.balanceNormalized),
            ) ?? undefined,
          calculatedShiftCount:
            playerSessionGroup.filter(ps => ps.hasPlayerData).length > 0
              ? getSum(
                  playerSessionGroup.map(ps => ps.calculatedShiftCount ?? 0),
                )
              : undefined,
          shiftTimeAvg:
            getMean(
              playerSessionGroup
                .filter(ps => ps.shiftTimeAvg)
                .map(ps => ps.shiftTimeAvg),
            ) ?? undefined,
          cumulative3DPlayerLoadV2:
            playerSessionGroup.filter(ps => ps.hasPlayerData).length > 0
              ? getSum(
                  playerSessionGroup.map(
                    ps => ps.cumulative3DPlayerLoadV2 ?? 0,
                  ),
                )
              : undefined,
          hustleScoreTopLevel:
            getMean(
              playerSessionGroup
                .filter(ps => ps.hustleScoreTopLevel)
                .map(ps => ps.hustleScoreTopLevel),
            ) ?? undefined,
          hrAvg:
            getMean(
              playerSessionGroup.filter(ps => ps.hrAvg).map(ps => ps.hrAvg),
            ) ?? undefined,
          hrMax:
            getMax(
              playerSessionGroup.filter(ps => ps.hrMax).map(ps => ps.hrMax),
            ) ?? undefined,
        })),
    [teamSessionGroupTeamSessions],
  );

  const teamSession = useMemo(
    () =>
      teamSessionGroupTeamSessions?.length
        ? teamSessionGroupTeamSessions[0]
        : null,
    [teamSessionGroupTeamSessions],
  );

  const displayStartTime = useMemo(() => {
    if (teamSession?.localDisplayStartTime) {
      return `${DateTime.fromFormat(
        teamSession.localDisplayStartTime,
        'M/d/yyyy h:mm a',
      ).toFormat('EEEE M/d/yyyy h:mm a')} ${teamSession?.timeZone ?? ''}`;
    }
    if (teamSession?.startTime) {
      return DateTime.fromISO(teamSession.startTime).toFormat(
        'EEEE M/d/yyyy h:mm a ZZZZ',
      );
    }
    return '';
  }, [teamSession]);
  const finalTeamSession = useMemo(
    () =>
      teamSessionGroupTeamSessions?.length
        ? [...teamSessionGroupTeamSessions].sort(
            (a: TeamSession, b: TeamSession) => {
              const aStartTime = a.startTime ? new Date(a.startTime) : null;
              const bStartTime = b.startTime ? new Date(b.startTime) : null;
              if (!aStartTime || !bStartTime) {
                return 0;
              }
              return bStartTime.getTime() - aStartTime.getTime();
            },
          )[0]
        : null,
    [teamSessionGroupTeamSessions],
  );

  if (teamSessionGroupStatus === 'loading' || !teamSessionGroup) {
    return (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <FullScreenLoadingSpinner />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container>
        <PageTitle>{t('pages.teamSessionGroupDetail')}</PageTitle>
        <BackButton navigationUrl={`/teams/${teamId}/teamsessions`}>
          {t('navigation.back', {pageName: t('pages.teamSessions')})}
        </BackButton>
        <SubtitleContainer>
          <PageSubtitle>
            <StyledSessionType>{sessionType}</StyledSessionType>
            {displayStartTime}
          </PageSubtitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              justifyContent: 'flex-end',
            }}>
            <ExportSessionModal
              buttonSx={{mb: 2, alignSelf: 'center'}}
              teamSessionGroupId={teamSessionGroup.id as number}
              teamSessionGroupStartTime={teamSessionGroup.startTime}
              teamSessionHasPuckDropEvents={
                (teamSessionGroupTeamSessions ?? [])
                  .flatMap(ts => ts.teamSessionSessionEvents ?? [])
                  .filter(
                    (e: TeamSessionSessionEvent) =>
                      [
                        'PUCK_DROP_PERIOD_1',
                        'PUCK_DROP_PERIOD_2',
                        'PUCK_DROP_PERIOD_3',
                        'PUCK_DROP_OVERTIME',
                      ].indexOf(e.sessionEvent?.code as string) !== -1,
                  ).length > 0
              }
              teamHasHudlConfig={activeTeam?.hasHudlConfig}
              teamHasSpiideoConfig={activeTeam?.hasSpiideoConfig}
              teamHasCatapultConfig={activeTeam?.hasCatapultConfig}
            />
          </Box>
        </SubtitleContainer>
        {sessionType === 'game' && finalTeamSession && (
          <SessionMetaData disableEdit teamSession={finalTeamSession} />
        )}
        <TeamShiftUtilization
          teamSessions={teamSessionGroupTeamSessions ?? []}
        />
        {sessionType !== 'game' && (
          <PracticeEfficiency
            playerSessions={teamSession?.playerSessions || []}
            teamSessionStartTime={teamSession?.startTime || ''}
            teamSessionEndTime={teamSession?.endTime || ''}
            practiceEfficiencyMax={activeTeam?.practiceEfficiencyMax}
          />
        )}
        <TeamSessionPlayerLoad
          teamSessions={teamSessionGroup?.teamSessions ?? []}
        />
        <TeamSessionsList
          teamSessions={teamSessionGroupTeamSessions ?? []}
          teamSize={
            (activeTeam?.players ?? []).filter(player => player.isActive).length
          }
          isTeamSessionGroup={true}
        />
        <TeamSessionPlayerList
          teamId={teamIdInt}
          teamSessionGroupId={teamSessionGroupIdInt}
          preferredUnits={preferredUnits}
          sessionType={sessionType || undefined}
          data={groupedPlayerSessions ?? []}
        />
      </Container>
    </>
  );
};

export default TeamSessionGroupDetail;
